import { useAssetId } from "@/setup/hooks/useAssetId";
import type { UtilityTarget } from "@/shared/models/UtilityTarget";
import { useUtilityTargets } from "@/shared/selectors/UtilityTargetSelector";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useRef } from "react";

const getSelectedTargetId = (
	param: string | null,
	targets: UtilityTarget[] | undefined,
	assetTargetId: number | null,
) => {
	const knownTargets = targets ?? [];
	const fallbackTargetId = assetTargetId ?? knownTargets[0]?.TargetId ?? null;
	if (param === null) return fallbackTargetId;

	const requestedTargetId = Number.parseInt(param, 10);
	const isKnown = knownTargets.some(
		(target) => target.TargetId === requestedTargetId,
	);

	return isKnown ? requestedTargetId : fallbackTargetId;
};

export const useUtilityTargetSelection = () => {
	const assetId = useAssetId();
	const { data: targets } = useUtilityTargets();
	const [param, setParam] = useQueryState("target", parseAsString);
	const previousAssetId = useRef(assetId);

	const assetTargetId =
		assetId === undefined
			? null
			: ((targets ?? []).find((target) => target.AssetId === assetId)
					?.TargetId ?? null);

	useEffect(() => {
		const previous = previousAssetId.current;
		previousAssetId.current = assetId;

		if (previous === undefined || previous === assetId) return;
		setParam(null);
	}, [assetId, setParam]);

	return {
		targetId: getSelectedTargetId(param, targets, assetTargetId),
		setTargetId: (targetId: number | null) =>
			setParam(targetId === null ? null : String(targetId)),
	};
};
